/**
 * Anonymous usage analytics — sized so it can't slow the app down.
 *
 * Every `track…` call is fire-and-forget: the event is appended to an
 * in-memory queue and returns synchronously. The queue is flushed:
 *   - on a 10-second timer,
 *   - when the queue hits MAX_QUEUE (the back-stop for very chatty
 *     pages), and
 *   - on `visibilitychange → hidden` and `pagehide` via
 *     `navigator.sendBeacon`, so events the user has done seconds
 *     before navigating away still make it to the server without
 *     blocking the unload.
 *
 * The "distinct user" identifier is a UUID stored in localStorage —
 * the only meaningful unit of "who" when the auth gate is a single
 * shared password and there are no individual accounts. It's an
 * opaque token, no PII, and the user can wipe it via the browser's
 * site-data controls.
 *
 * If anything in here throws, it must NEVER bubble up to the caller.
 * Analytics MUST NOT break UX.
 */

const STORAGE_KEY = "tbm_anon_id";
const ENDPOINT = "/api/analytics/events";
const FLUSH_INTERVAL_MS = 10_000;
const MAX_QUEUE = 50;

type EventType = "page_view" | "feature" | "login";

interface QueuedEvent {
  ts: string;
  type: EventType;
  page?: string;
  name?: string;
  userId: string;
}

const queue: QueuedEvent[] = [];
let userId: string | null = null;
let started = false;

function makeUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `anon-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

function readOrCreateUserId(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const id = makeUuid();
    localStorage.setItem(STORAGE_KEY, id);
    return id;
  } catch {
    // localStorage blocked (private mode, storage quota) — fall back to
    // an in-memory id so we still get a per-session count.
    return `anon-session-${makeUuid()}`;
  }
}

function ensureUserId(): string {
  if (userId == null) userId = readOrCreateUserId();
  return userId;
}

function flush(useBeacon: boolean): void {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  const payload = JSON.stringify({ events: batch });
  try {
    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(
        ENDPOINT,
        new Blob([payload], { type: "application/json" }),
      );
    } else {
      // `keepalive: true` lets the request survive the document being
      // torn down — important for the rare timer flush that lands just
      // as the user navigates.
      void fetch(ENDPOINT, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {
        // Ignore — analytics failures must never reach the user.
      });
    }
  } catch {
    // Same — swallow anything that escapes the calls above.
  }
}

/**
 * Identifier exposed for routes that need to forward it server-side —
 * notably the login mutation, which carries this id so the login event
 * can be attributed to a specific browser.
 */
export function getAnonId(): string {
  return ensureUserId();
}

/**
 * One-time install of the background flush timer and the page-hide
 * hooks. Idempotent; cheap to call on every render.
 */
export function startAnalytics(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  ensureUserId();
  window.setInterval(() => flush(false), FLUSH_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
  window.addEventListener("pagehide", () => flush(true));
}

export function trackPageView(page: string): void {
  if (typeof window === "undefined") return;
  queue.push({
    ts: new Date().toISOString(),
    type: "page_view",
    page,
    userId: ensureUserId(),
  });
  if (queue.length >= MAX_QUEUE) flush(false);
}

/** Record a discrete feature interaction (chip toggle, export, etc.). */
export function trackFeature(name: string, page?: string): void {
  if (typeof window === "undefined") return;
  queue.push({
    ts: new Date().toISOString(),
    type: "feature",
    name,
    page,
    userId: ensureUserId(),
  });
  if (queue.length >= MAX_QUEUE) flush(false);
}
